import { DSSpacer, DSText } from '@zoe/ds-web'
import React, { useContext } from 'react'

import { FragmentComponent } from '@/types/graphql'

import { TrackingContext } from '@/context/trackingContext'

import { DSGrid, DSGridBlock } from '@/components/ds/Grid/Grid'
import { DSLinkButton } from '@/components/ds/LinkButton/LinkButton'

import { trackCtaClick } from '@/lib/gtm/gtmV2'

type CtaRecord = {
  __typename: 'CtaRecord'
  id: string
  label: string
  url: string
  newTab?: boolean
}

export type LinkCollectionRecord = {
  __typename: 'LinkCollectionRecord'
  id: string
  title?: string
  section1Title?: string
  section2Title?: string
  section1Links?: CtaRecord[]
  section2Links?: CtaRecord[]
  dataTestId?: string
}

const CtaFragment = `
  fragment CtaRecordFragment on CtaRecord {
    __typename
    id
    label
    url
    newTab
  }
`

const fragment = `
  fragment LinkCollectionBlockFragment on LinkCollectionRecord {
    __typename
    id
    title
    section1Title
    section1Links {
      ...CtaRecordFragment
    }
    section2Title
    section2Links {
      ...CtaRecordFragment
    }
    dataTestId
  }
  ${CtaFragment}
`

export const LinkCollectionBlock: FragmentComponent<{}, LinkCollectionRecord> = ({ record }) => {
  const { section } = useContext(TrackingContext)
  const handleOnPress = ({ label }) => {
    trackCtaClick(section || 'cms', label || '')
  }

  return (
    <DSGrid
      dataTestId={record?.dataTestId}
      backgroundColor="cream-subtle"
      mobileBackgroundColor="cream-subtle"
      backgroundFullWidth
    >
      <DSSpacer direction="vertical" size={144} breakAt={{ m: 72 }} />
      {record.title && (
        <DSGridBlock
          breakConfig={{
            s: { colSpan: 2 },
            m: { colSpan: 6 },
            l: {
              colSpan: 4,
            },
            xl: {
              colSpan: 4,
            },
          }}
        >
          <DSText
            as="h5"
            variant="fluid-heading-900"
            weight="book"
            breakAt={{ breakPoint: 'm', variant: 'fluid-heading-900', align: 'center' }}
          >
            {record.title}
          </DSText>
          <DSSpacer direction="vertical" size={40} />
        </DSGridBlock>
      )}
      {record.section1Links && (
        <DSGridBlock
          breakConfig={{
            s: { colSpan: 2 },
            m: { colSpan: 6 },
            l: {
              colSpan: 3,
              offset: 1,
            },
            xl: {
              colSpan: 3,
              offset: 1,
            },
          }}
        >
          {record.section1Title && (
            <>
              <DSText as="p" variant="fluid-eyebrow-100" breakAt={{ breakPoint: 'm', align: 'center' }}>
                {record.section1Title}
              </DSText>
              <DSSpacer direction="vertical" size={32} />
            </>
          )}
          {record.section1Links.map((cta: CtaRecord, i) => (
            <React.Fragment key={`section1links-${i}`}>
              <DSLinkButton
                variant="secondary"
                colourScheme="default"
                size="small"
                width="full"
                href={cta.url}
                target={cta.newTab ? '_blank' : '_self'}
                onPress={() => handleOnPress({ label: cta.label })}
              >
                <DSText as="span" variant="fluid-paragraph-200" weight="semiBold">
                  {cta.label}
                </DSText>
              </DSLinkButton>
              <DSSpacer direction="vertical" size={16} />
            </React.Fragment>
          ))}
        </DSGridBlock>
      )}

      {record.section2Links && (
        <DSGridBlock
          breakConfig={{
            s: { colSpan: 2 },
            m: { colSpan: 6 },
            l: {
              colSpan: 3,
              offset: 1,
            },
            xl: {
              colSpan: 3,
              offset: 1,
            },
          }}
        >
          <DSSpacer direction="vertical" size={0} breakAt={{ m: 72 }} />
          {record.section2Title && (
            <>
              <DSText as="p" variant="fluid-eyebrow-100" breakAt={{ breakPoint: 'm', align: 'center' }}>
                {record.section2Title}
              </DSText>
              <DSSpacer direction="vertical" size={32} />
            </>
          )}
          {record.section2Links.map((cta: CtaRecord, i) => (
            <React.Fragment key={`section2links-${i}`}>
              <DSLinkButton
                variant="secondary"
                colourScheme="default"
                size="small"
                width="full"
                href={cta.url}
                target={cta.newTab ? '_blank' : '_self'}
                onPress={() => handleOnPress({ label: cta.label })}
              >
                <DSText as="span" variant="fluid-paragraph-200" weight="semiBold">
                  {cta.label}
                </DSText>
              </DSLinkButton>
              <DSSpacer direction="vertical" size={16} />
            </React.Fragment>
          ))}
        </DSGridBlock>
      )}
      <DSSpacer direction="vertical" size={144} breakAt={{ m: 72 }} />
    </DSGrid>
  )
}

LinkCollectionBlock.fragment = fragment
LinkCollectionBlock.recordName = 'LinkCollectionRecord'
